/**
 * ClapGenreProcessor — main-thread wrapper around clap-worker.js.
 *
 * Public surface:
 *
 *   window.ClapGenreProcessor.pickVariant()
 *     → 'fp32' | 'int8' based on navigator.deviceMemory / hardwareConcurrency.
 *
 *   await window.ClapGenreProcessor.warm({ variant, onProgress })
 *     → loads the chosen ONNX into the worker. variant defaults to pickVariant().
 *
 *   await window.ClapGenreProcessor.embedAudio(audioBuffer, { signal, onProgress, windowPositions })
 *     → resamples to 48 kHz mono, samples N × 10 s windows. By default the
 *       sampling positions are [0.25, 0.50, 0.75] of the track (or [0.50]
 *       for tracks < 30 s). Pass `windowPositions: [0.1, 0.4, 0.7, 0.9]`
 *       to override — the user-facing waveform UI uses this to let the
 *       listener re-analyze with custom marker placements. Computes the
 *       log-mel spectrogram via window.ClapMel for each window (must be
 *       loaded — clap-mel.js), runs the encoder on each, mean-pools +
 *       L2-normalizes, returns Float32Array(512).
 *
 *   window.ClapGenreProcessor.classify(audioEmbedding, textEmbeddingsDoc)
 *     → main-thread dot product against precomputed text embeddings.
 *       Returns { genre: [{slug, score, tier, parent}], mood: [...],
 *                 instrument: [...] }, each sorted descending by score.
 *
 * Why not put classify() in the worker too: it's ~64 × 512 multiplies =
 * ~32 k floats, <1 ms even on a Pentium. Worker round-trip postMessage
 * latency dwarfs the math. Keeping it main-thread also means the JSON
 * stays in one place (no need to ship to the worker every time).
 *
 * Variant choice: int8 (~55 MB) for low-memory / mobile, fp32 (~180 MB)
 * for desktop. The cosine-similarity precision difference between the
 * two is ~0.01 in benchmarks — meaningful only on borderline pairs.
 * Mirror of Apollo's POOL_SIZE capability gate (apollo-processor.js:105).
 */
(function(){
'use strict';

// ── Public name guard ──────────────────────────────────────────────────────
// Don't shadow a native global. ClapGenreProcessor is unambiguously our own
// per [feedback_no_native_global_overwrite].
if (typeof window === 'undefined') return;

// ── ORT runtime: the owner answers which build (window.__ortBuild) ─────────
// The worker runs only the wasm (CPU) provider and loads ORT for itself, so it
// asks with `cpuOnly`: the plain build on every engine. Until 29.10.2026 this file
// hard-coded ort.min.js — the JSEP build, onnxruntime#26827 on WebKit (refund
// request #56: iPhone Safari lost half the analyses while the model was built).
// Measured, CLAP int8, Δ peak working set of the browser process tree:
//   WebKit    JSEP +961…+962 MB (and growing after the end)   plain +364…+488 MB
//   Chromium  JSEP +463 MB, build 8.2 s                      plain +294 MB, 5.2 s
// (tests/scratch/mgd-ort-build-bench-scratch.mjs, docs/why/clap-model-memory.md).
function ortBuild(){
    try {
        if (window.__ortBuild && window.__ortBuild.pick) {
            var p = window.__ortBuild.pick({ cpuOnly: true });
            return { primary: p.url, fallback: p.fallbackUrl, wasmBase: p.wasmBase };
        }
    } catch (_) {}
    // Owner absent (an old cached page): the plain build, same directory.
    return { primary: '/vendor/onnxruntime/ort.wasm.min.js', fallback: null, wasmBase: '/vendor/onnxruntime/' };
}

var CLAP_SR = 48000;
var CLAP_WINDOW_SEC = 10;
var CLAP_WINDOW_SAMPLES = CLAP_SR * CLAP_WINDOW_SEC;  // 480_000

// ── Variant catalogue ──────────────────────────────────────────────────────
// metaName / metaFallback are read off the page by variantUrls() to support
// AssetRouter::heavy / heavyFallback routing (RU origin vs HF mirror).
var MODEL_CATALOGUE = {
    fp32: {
        name: 'fp32',
        primary:  '/vendor/onnx/clap-music-audio-encoder-fp32.onnx',
        cdn:      'https://timbrica.com/vendor/onnx/clap-music-audio-encoder-fp32.onnx',
        metaName: 'clap-audio-model-url-fp32',
        metaFallback: 'clap-audio-model-url-fp32-fallback',
        sizeMB: 180,
    },
    int8: {
        name: 'int8',
        primary:  '/vendor/onnx/clap-music-audio-encoder-int8.onnx',
        cdn:      'https://timbrica.com/vendor/onnx/clap-music-audio-encoder-int8.onnx',
        metaName: 'clap-audio-model-url-int8',
        metaFallback: 'clap-audio-model-url-int8-fallback',
        sizeMB: 74,
        /* The same model as graph + weights (clap-worker.js createSplitSession):
           ORT builds it with 86 MB less wasm heap — the phone's tab outlives the
           build. The one-file model above stays the fallback. Both files are cut
           from it by scripts/export-clap-audio-onnx.py (--split). */
        split: {
            graph: '/vendor/onnx/clap-music-audio-encoder-int8.graph.onnx',
            data:  '/vendor/onnx/clap-music-audio-encoder-int8.weights.bin',
            metaGraph: 'clap-audio-model-url-int8-graph',
            metaData:  'clap-audio-model-url-int8-weights',
            path: 'clap-music-audio-encoder-int8.weights.bin',
        },
    },
};

function splitUrls(variant){
    var spec = MODEL_CATALOGUE[variant];
    if (!spec || !spec.split) return null;
    var s = spec.split;
    var pick = function(metaName, dflt){
        var out = [];
        if (typeof document !== 'undefined') {
            var meta = document.querySelector('meta[name="' + metaName + '"]');
            if (meta && meta.content) out.push(meta.content);
        }
        if (out.indexOf(dflt) === -1) out.push(dflt);
        return out;
    };
    return { graph: pick(s.metaGraph, s.graph), data: pick(s.metaData, s.data), path: s.path };
}

function variantUrls(variant){
    var spec = MODEL_CATALOGUE[variant] || MODEL_CATALOGUE.fp32;
    var urls = [];
    var pushIfNew = function(u){ if (u && urls.indexOf(u) === -1) urls.push(u); };
    if (typeof document !== 'undefined') {
        var meta = document.querySelector('meta[name="' + spec.metaName + '"]');
        var metaFb = document.querySelector('meta[name="' + spec.metaFallback + '"]');
        if (meta && meta.content) pushIfNew(meta.content);
        if (metaFb && metaFb.content) pushIfNew(metaFb.content);
    }
    pushIfNew(spec.primary);
    pushIfNew(spec.cdn);
    return urls;
}

// ── Worker lifecycle ────────────────────────────────────────────────────────
var _worker = null;
var _workerInitPromise = null;
var _pending = {};
var _nextMsgId = 1;
var _loadPromiseByVariant = {};

function _resolveWorkerUrl(){
    // Defensive same-origin check — `new Worker(crossOriginUrl)` throws
    // synchronously without explicit CORS+nosniff headers we don't emit
    // on the assets CDN. If a blade ever sets the meta via the wrong
    // AssetRouter helper (light/heavy instead of worker), the wrong URL
    // would crash every analyze attempt. Falling through to the same-
    // origin default keeps the tool alive at the cost of a bytes-on-CDN
    // benefit we never actually use for workers (~11 KB file, loaded
    // once per session). Telemetry pre-fix 2026-06-12: 13 users / 30 d
    // hit "Failed to construct 'Worker': cannot be accessed from origin".
    if (typeof document !== 'undefined') {
        var meta = document.querySelector('meta[name="clap-worker-url"]');
        if (meta && meta.content) {
            var url = meta.content;
            if (typeof URL === 'function' && typeof location !== 'undefined') {
                try {
                    var parsed = new URL(url, location.href);
                    if (parsed.origin === location.origin) return url;
                    // Cross-origin meta — silently fall through to same-
                    // origin default; never throw here, this runs on every
                    // page load.
                } catch (_) {
                    // Malformed URL — fall through.
                }
            } else {
                return url;
            }
        }
    }
    return '/js/clap-worker.js';
}

function _initWorker(){
    if (_workerInitPromise) return _workerInitPromise;
    _workerInitPromise = new Promise(function(resolve, reject){
        try {
            var w = new Worker(_resolveWorkerUrl());
            _worker = w;
            w.onmessage = _onMessage;
            w.onerror = function(ev){
                console.error('[CLAP] worker error:', ev.message || ev);
            };
            var id = _nextMsgId++;
            _pending[id] = { resolve: function(){ resolve(true); }, reject: reject };
            var ob = ortBuild();
            w.postMessage({ id: id, type: 'init',
                primary: ob.primary, fallback: ob.fallback, wasmBase: ob.wasmBase });
        } catch (e) { reject(e); }
    });
    return _workerInitPromise;
}

function _onMessage(ev){
    var m = ev.data || {};
    var p = _pending[m.id];
    if (!p) return;
    switch (m.type) {
        case 'progress':
            if (p.onProgress) {
                try { p.onProgress(m.ratio, m.stage, m); } catch(_){}
            }
            break;
        case 'ready':
            delete _pending[m.id];
            p.resolve(m);
            break;
        case 'result':
            delete _pending[m.id];
            p.resolve(m);
            break;
        case 'error':
            delete _pending[m.id];
            var err = new Error(m.message || 'CLAP worker error');
            err.stack = m.stack || '';
            /* The worker names a cancel. Guessing it from the text renamed a
               dying runtime («RuntimeError: Aborted(…)») a cancel too, and the
               caller skipped reportError. */
            if (m.name === 'AbortError') err.name = 'AbortError';
            if (m.code) err.code = m.code;
            p.reject(err);
            break;
    }
}

function _sendToWorker(type, payload, opts){
    opts = opts || {};
    return _initWorker().then(function(){
        return new Promise(function(resolve, reject){
            var id = _nextMsgId++;
            _pending[id] = {
                resolve: resolve, reject: reject,
                onProgress: opts.onProgress || null,
            };
            var msg = { id: id, type: type };
            for (var k in payload) if (Object.prototype.hasOwnProperty.call(payload, k)) msg[k] = payload[k];
            if (opts.signal) {
                if (opts.signal.aborted) {
                    delete _pending[id];
                    var ae = new Error('CLAP aborted before start');
                    ae.name = 'AbortError';
                    reject(ae);
                    return;
                }
                opts.signal.addEventListener('abort', function(){
                    try { _worker && _worker.postMessage({ type: 'abort' }); } catch(_){}
                }, { once: true });
            }
            if (opts.transfer) _worker.postMessage(msg, opts.transfer);
            else _worker.postMessage(msg);
        });
    });
}

// ── Telemetry ───────────────────────────────────────────────────────────────
function reportTelemetry(slug, action, payload){
    try {
        if (typeof window.__ae === 'function') {
            var p = { slug: slug || 'music-genre-detector', action: action };
            for (var k in payload) if (Object.prototype.hasOwnProperty.call(payload, k)) p[k] = payload[k];
            window.__ae('feature_use', p);
        }
    } catch(_){}
}

function reportError(slug, error_type, msg){
    try {
        if (typeof window.__aeToolError === 'function') {
            window.__aeToolError({
                slug: slug || 'music-genre-detector',
                error_type: error_type,
                msg: (msg || '').substring(0, 200),
            });
        }
    } catch(_){}
}

// ── Resampler (linear) ─────────────────────────────────────────────────────
// CLAP needs 48 kHz mono. Most browsers decodeAudioData at the device's
// native rate (typically 44100 or 48000) — for 48 kHz inputs this is a
// no-op, for 44.1 kHz we resample. Linear interpolation is fine for ML
// feature extraction; mel spectrogram is robust to ±1 sample alignment
// drift. We don't ship a polyphase filter just for this.
function resampleMonoLinear(input, fromRate, toRate){
    if (fromRate === toRate) return input;
    var ratio = fromRate / toRate;
    var outLen = Math.floor(input.length / ratio);
    var out = new Float32Array(outLen);
    for (var i = 0; i < outLen; i++) {
        var srcF = i * ratio;
        var s0 = Math.floor(srcF);
        var s1 = Math.min(s0 + 1, input.length - 1);
        var frac = srcF - s0;
        out[i] = input[s0] * (1 - frac) + input[s1] * frac;
    }
    return out;
}

function audioBufferToMono48k(audioBuffer){
    var ch = audioBuffer.numberOfChannels;
    var n = audioBuffer.length;
    var mono = new Float32Array(n);
    if (ch === 1) {
        mono.set(audioBuffer.getChannelData(0));
    } else {
        // Average all channels (more accurate stereo→mono than just L+R).
        for (var c = 0; c < ch; c++) {
            var data = audioBuffer.getChannelData(c);
            for (var i = 0; i < n; i++) mono[i] += data[i];
        }
        var inv = 1 / ch;
        for (var j = 0; j < n; j++) mono[j] *= inv;
    }
    return resampleMonoLinear(mono, audioBuffer.sampleRate, CLAP_SR);
}

// ── Window sampling ────────────────────────────────────────────────────────
// Strategy:
//   * If `customPositions` is supplied → use those exact fractions [0..1]
//     (user-controlled re-analyze with dragged/added markers).
//   * Else, duration < 30 s → single center window (or pad to 10 s if shorter).
//   * Else, duration ≥ 30 s → 3 windows at 25 %, 50 %, 75 % of the track.
//
// Each window is exactly CLAP_WINDOW_SAMPLES centred on the position fraction.
// If a window's centre would land too close to either edge, the start clamps
// to keep the window inside the track — the marker still moves visually but
// the analyzed region stays within bounds.
function pickWindows(monoSamples, customPositions){
    var total = monoSamples.length;
    var W = CLAP_WINDOW_SAMPLES;
    var positions;
    if (customPositions && customPositions.length) {
        positions = customPositions;
    } else if (total < CLAP_SR * 30) {
        var startC = Math.max(0, Math.floor((total - W) / 2));
        return [extractWindow(monoSamples, startC)];
    } else {
        // Shifted from [0.25, 0.50, 0.75] 2026-06-11 — electronic-music
        // intros (and pop "drops") commonly run 20–40 s. On a 169-second
        // track 0.25 = 42 s in, which is still climbing on EDM; 0.35 = 59 s
        // is past most drops. The last window stays at 0.75 to avoid outros
        // (the second half of a track is usually still in steady-state
        // when the last quarter starts).
        positions = [0.35, 0.55, 0.75];
    }
    var out = [];
    for (var i = 0; i < positions.length; i++) {
        var p = positions[i];
        if (p < 0) p = 0;
        if (p > 1) p = 1;
        var start = Math.max(0, Math.min(total - W, Math.floor(total * p - W / 2)));
        out.push(extractWindow(monoSamples, start));
    }
    return out;
}

function extractWindow(samples, start){
    var W = CLAP_WINDOW_SAMPLES;
    var win = new Float32Array(W);
    var copyLen = Math.min(W, samples.length - start);
    for (var i = 0; i < copyLen; i++) win[i] = samples[start + i];
    // Trailing zeros if copyLen < W — fine for the model, sees as silence.
    return win;
}

function l2Normalize(vec){
    var sum = 0;
    for (var i = 0; i < vec.length; i++) sum += vec[i] * vec[i];
    var norm = Math.sqrt(sum) || 1;
    var inv = 1 / norm;
    var out = new Float32Array(vec.length);
    for (var j = 0; j < vec.length; j++) out[j] = vec[j] * inv;
    return out;
}

function meanPoolEmbeddings(list){
    if (!list.length) throw new Error('meanPoolEmbeddings: empty list');
    var D = list[0].length;
    var sum = new Float32Array(D);
    for (var k = 0; k < list.length; k++) {
        for (var i = 0; i < D; i++) sum[i] += list[k][i];
    }
    return l2Normalize(sum);
}

// ── Public API ──────────────────────────────────────────────────────────────
var Api = {
    /** Pick fp32 / int8 based on device capability. */
    pickVariant: function(){
        try {
            var mem = navigator.deviceMemory || 0;
            var cpu = navigator.hardwareConcurrency || 0;
            if (mem >= 8 && cpu >= 4) return 'fp32';
        } catch(_) {}
        return 'int8';
    },

    /** Warm the worker + load model. Resolves when ready. */
    warm: function(opts){
        opts = opts || {};
        var variant = opts.variant || Api.pickVariant();
        if (_loadPromiseByVariant[variant]) return _loadPromiseByVariant[variant];
        var urls = variantUrls(variant);
        var slug = opts.slug || 'music-genre-detector';
        var startMs = Date.now();
        // 2026-06-04 cross-tool audit: emit warm_started so warm-conversion
        // funnels work on the ONNX cohort (existing clap_model_loaded fires
        // only on COMPLETE — invisible to the abandonment cohort).
        reportTelemetry(slug, 'warm_started', { variant: variant, stage: 'model_fetch' });
        var promise = _sendToWorker('load', { variant: variant, urls: urls, split: splitUrls(variant) }, {
            onProgress: opts.onProgress,
        }).then(function(res){
            reportTelemetry(slug, 'clap_model_loaded', {
                variant: variant, sizeMB: res.sizeMB, compileMs: res.compileMs,
                cached: !!res.cached, totalMs: Date.now() - startMs,
                fallbackIdx: res.fallbackIdx,
                // Fetch resilience signal: how many same-URL retries the
                // download needed, and a per-URL host+failure trail. A rising
                // retries>0 / non-empty trail rate flags a degrading CDN path
                // (the diagnostic that was missing when Алексей's idx0+idx1
                // burned silently — req #745, 2026-06-14).
                retries: res.retries || 0,
                trail: (res.trail && res.trail.length) ? res.trail : undefined,
                // 1 = graph + weights; 0 with splitErr = the pair failed and the
                // one-file model was built instead (its wasm heap is 86 MB larger).
                split: res.split ? 1 : 0,
                splitErr: res.splitErr || undefined,
            });
            // Mirror the AMC pattern: a canonical 'warm_done' event so cross-tool
            // dashboards can query a single event name across the ONNX cohort.
            reportTelemetry(slug, 'warm_done', {
                variant: variant, ms: Date.now() - startMs, sizeMB: res.sizeMB,
            });
            return res;
        }).catch(function(err){
            _loadPromiseByVariant[variant] = null;  // allow retry
            reportError(slug, 'clap_model_load_failed', err && err.message);
            throw err;
        });
        _loadPromiseByVariant[variant] = promise;
        return promise;
    },

    /**
     * Run end-to-end audio → 512-d embedding.
     * audioBuffer: decoded AudioBuffer (any sample rate, any channels).
     */
    embedAudio: function(audioBuffer, opts){
        opts = opts || {};
        var slug = opts.slug || 'music-genre-detector';
        var t0 = Date.now();
        var mono48 = audioBufferToMono48k(audioBuffer);
        var windows = pickWindows(mono48, opts.windowPositions);
        var resampleMs = Date.now() - t0;

        if (!window.ClapMel || typeof window.ClapMel.compute !== 'function') {
            throw new Error('ClapGenreProcessor.embedAudio: window.ClapMel.compute missing — clap-mel.js not loaded');
        }

        var perWindowEmbeddings = [];
        var seqPromise = Promise.resolve();
        var nWin = windows.length;
        windows.forEach(function(win, idx){
            seqPromise = seqPromise.then(function(){
                // Mel-spec computed main-thread (~5 ms per 480k-sample window).
                // The audio encoder ONNX expects input_features of shape
                // [B=1, 1, T_frames=1001, n_mels=64] = 64_064 floats.
                var inputFeatures = window.ClapMel.compute(win);
                return _sendToWorker('embed', { inputFeatures: inputFeatures }, {
                    transfer: [inputFeatures.buffer],
                    signal: opts.signal,
                    onProgress: function(ratio, stage, msg){
                        if (opts.onProgress) {
                            // Map per-window progress to overall 0..1.
                            var overall = (idx + ratio) / nWin;
                            opts.onProgress(overall, stage, { window: idx + 1, totalWindows: nWin });
                        }
                    },
                });
            }).then(function(res){
                perWindowEmbeddings.push(res.embedding);
            });
        });

        return seqPromise.then(function(){
            reportTelemetry(slug, 'clap_inference_complete', {
                windows: nWin, resampleMs: resampleMs,
                totalMs: Date.now() - t0,
                durationS: Math.round(audioBuffer.duration),
            });
            // Return the array of per-window embeddings. The classifier
            // does confidence-weighted score-pooling internally, which
            // outperforms the previous uniform-mean embedding pool — a
            // silent or transient window contributes proportionally less
            // to the final classification than a strong, distinctive one.
            // Backward-compat: classify() accepts either Float32Array
            // (single) or Float32Array[] (windowed).
            return perWindowEmbeddings;
        }).catch(function(err){
            if (err && err.name === 'AbortError') throw err;
            reportError(slug, 'clap_inference_failed', err && err.message);
            throw err;
        });
    },

    /**
     * Classify a 512-d audio embedding against precomputed text embeddings.
     * textEmbeddingsDoc: the parsed JSON loaded from
     *   public/data/clap-text-embeddings.json
     *
     * Returns:
     *   {
     *     genre:      [{slug, score, tier, parent?}, ...]  // sorted desc, all 40+
     *     mood:       [{slug, score}, ...]                  // sorted desc, all 12
     *     instrument: [{slug, score}, ...]                  // sorted desc, all 10
     *   }
     */
    classify: function(audioEmbeddingOrArray, textEmbeddingsDoc){
        if (!textEmbeddingsDoc || !textEmbeddingsDoc.labels) {
            throw new Error('classify: textEmbeddingsDoc missing .labels');
        }
        // Accept either a single Float32Array (pooled embedding) OR an
        // array of Float32Arrays (per-window). For the array case we do
        // confidence-weighted score-level pooling: each window's
        // contribution to the final genre/mood/instrument scores is
        // weighted by how distinctive that window was (max-genre-score).
        //
        // Why weighted-pool over uniform-mean: a silent intro, a quiet
        // outro, or a brief silent section produces a low-magnitude
        // embedding whose dot products with every label cluster around
        // zero (the embedding has no direction). Uniformly averaging it
        // in dilutes the strong windows that DID see distinctive content.
        // Weighting by max-genre-score gives each window a vote
        // proportional to how confidently it could pick ANY genre.
        //
        // Math: score-level pooling and embedding-level pooling are
        // equivalent under L2-normalisation because dot product is linear:
        //   classify(weighted_mean(E)) == weighted_mean(classify(E_i))
        // So we pool at the score level, which is cheaper (no per-window
        // L2 renormalisation) and lets us compute the weights inline.
        var labels = textEmbeddingsDoc.labels;
        var isArray = Array.isArray(audioEmbeddingOrArray);
        var embeddings = isArray ? audioEmbeddingOrArray : [audioEmbeddingOrArray];
        if (!embeddings.length) throw new Error('classify: empty embeddings');
        var D = embeddings[0].length;

        // Per-window per-label scores.
        var perWindowScores = embeddings.map(function(emb){
            var s = {};
            for (var i = 0; i < labels.length; i++) {
                var lab = labels[i];
                var labEmb = lab.embedding;
                var dot = 0;
                for (var j = 0; j < D; j++) dot += emb[j] * labEmb[j];
                s[lab.slug] = dot;
            }
            return s;
        });

        // Per-window weight = max(genre score). Genre is the most
        // distinctive category (40+ labels with the widest separation in
        // embedding space) — a window that can't pick a confident genre
        // wouldn't help on mood/instruments either. Softmax with τ=0.05
        // keeps the weights from collapsing to winner-take-all while still
        // sharply discounting low-info windows.
        var weights = perWindowScores.map(function(ws){
            var max = -Infinity;
            for (var i = 0; i < labels.length; i++) {
                if (labels[i].category === 'genre') {
                    var v = ws[labels[i].slug];
                    if (v > max) max = v;
                }
            }
            return max;
        });
        var TAU = 0.05;
        var expWeights = weights.map(function(w){ return Math.exp(w / TAU); });
        var weightSum = 0;
        for (var i = 0; i < expWeights.length; i++) weightSum += expWeights[i];
        if (weightSum <= 0) weightSum = 1;

        // Weighted score-level pool.
        var pooled = {};
        for (var li = 0; li < labels.length; li++) {
            var slug = labels[li].slug;
            var acc = 0;
            for (var wi = 0; wi < perWindowScores.length; wi++) {
                acc += expWeights[wi] / weightSum * perWindowScores[wi][slug];
            }
            pooled[slug] = acc;
        }

        var out = { genre: [], mood: [], instrument: [] };
        for (var i = 0; i < labels.length; i++) {
            var lab = labels[i];
            var entry = { slug: lab.slug, score: pooled[lab.slug] };
            if (lab.tier != null)   entry.tier = lab.tier;
            if (lab.parent)         entry.parent = lab.parent;
            (out[lab.category] || (out[lab.category] = [])).push(entry);
        }
        out.genre.sort(function(a, b){ return b.score - a.score; });
        out.mood.sort(function(a, b){ return b.score - a.score; });
        out.instrument.sort(function(a, b){ return b.score - a.score; });
        return out;
    },

    /**
     * Compute the "confidence gap" for the top-1 result of a sorted list.
     * Returns 0..1 — proxy for how confidently the model picked top-1 over
     * top-2 (better signal than raw cosine which clusters tightly).
     *
     * Heuristic: (top1 - top2) / max(top1, 0.01), clamped to [0, 1].
     * Multiplied by 1.2 because the raw gaps tend to be small (CLAP audio
     * embeddings cluster), but capped at 1.
     */
    confidenceGap: function(sortedList){
        /* Реализация живёт в странице (window.ClapGenreConfidence): рендеру она
           нужна и без модели. Здесь — делегирование, чтобы формула была одна. */
        if (typeof window !== 'undefined' && window.ClapGenreConfidence) return window.ClapGenreConfidence.gap(sortedList);
        if (!sortedList || sortedList.length < 2) return 1;
        var g = (sortedList[0].score - sortedList[1].score) / Math.max(sortedList[0].score, 0.01) * 1.2;
        return g < 0 ? 0 : (g > 1 ? 1 : g);
    },

    /**
     * Map a confidence gap (0..1) to a 4-tier label key. Honest framing of
     * the model's uncertainty for top-1 results: a 0.528 cosine alone tells
     * the user nothing about how DECISIVE the pick was vs runners-up.
     * Tiers:
     *   gap ≥ 0.50 → 'strong'    — top-1 clearly dominates
     *   gap ≥ 0.25 → 'likely'    — top-1 is the best of several plausible matches
     *   gap ≥ 0.10 → 'possible'  — top-1 is just slightly ahead
     *   gap < 0.10 → 'ambiguous' — model couldn't decide; show top-3 candidly
     *
     * Thresholds calibrated on the same CLAP zero-shot setup we ship:
     * 40-genre vocabulary, mean-pool over 3 windows. The 0.10 floor matches
     * the gap distribution observed where users complained "не идеально"
     * (e.g. tool_comments #727 — top-1 cosine 0.528 with runner-up ~0.49 →
     * gap 0.087 → 'ambiguous'). Returns the bare key — caller looks up the
     * localised label.
     */
    confidenceTier: function(gap){
        if (typeof window !== 'undefined' && window.ClapGenreConfidence) return window.ClapGenreConfidence.tier(gap);
        if (gap >= 0.50) return 'strong';
        if (gap >= 0.25) return 'likely';
        if (gap >= 0.10) return 'possible';
        return 'ambiguous';
    },
};

window.ClapGenreProcessor = Api;

})();
